import React, { useMemo, useState } from 'react';

import {
  dashboardDeliveryRequestApis,
  dashboardDeliveryRequestURLSubmitApis,
} from '@apis/next/deliveryRequestApis';
import { dashboardDeliveryRequestLogApis } from '@apis/next/deliveryRequestLogApis';
import { dashboardPartnerApis } from '@apis/next/partnerApis';
import copySvg from '@assets/img/icons/copy.svg';
import { PartnerLink } from '@components/admin/widgets/PartnerLink';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogContent,
  ActionableDialogTitle,
} from '@components/Dialog';
import { IndexedFormContainer, IndexedLabel } from '@components/NextForms/IndexedForm';
import { TextInput } from '@components/NextForms/TextInput';
import { QueryContainer } from '@components/QueryContainer';
import { useFormUtils } from '@hooks/useFormUtils';
import {
  DeliveryRequestURLSubmitForm,
  type DeliveryRequest,
  DeliveryRequestURLSubmitResult,
} from '@models/next/DeliveryRequest';
import { DeliveryRequestHelper } from '@models/next/DeliveryRequestHelper';
import { Partner } from '@models/next/Partner';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { useQueryClient } from '@tanstack/react-query';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import copy from 'clipboard-copy';
import clsx from 'clsx';

const strings = createStrings({
  ja: {
    title: 'リンクをコピー',
    cannotURLSubmitDeliveryRequest: `下記の原因で処理できません：`,
    deliveryURLsCreatedPrompt: '配車資料のURLが作成しました：',
    copySuccessMessage: 'リンクをコピーしました。',
    create: '作成',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  dialogContent: {
    ...common.flexColumn,
    rowGap: '1rem',
    padding: '2.5rem 2rem',
    flexShrink: 1,
    minHeight: 0,
    maxHeight: 550,
  },
}));

export interface DeliveryRequestURLSubmitDialogProps {
  deliveryRequest: DeliveryRequest;
  open: boolean;
  onClose: () => void;
}

const DeliveryRequestURLSubmitDialogContent: React.FC<
  DeliveryRequestURLSubmitDialogProps & {
    partners: Partner[];
    onSubmitSuccess: (r: DeliveryRequestURLSubmitResult[]) => void;
  }
> = ({ deliveryRequest, partners, onSubmitSuccess, onClose }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const queryClient = useQueryClient();

  const deliveryRequestURLSubmit = dashboardDeliveryRequestURLSubmitApis.usePut(
    '/dashboard/delivery-request',
    {
      queries: {
        action: 'BATCH_URL_SUBMIT',
        id: [deliveryRequest.id],
      },
    },
    {
      onSuccess() {
        queryClient.invalidateQueries(
          dashboardDeliveryRequestApis.getKeyByPath('get', '/dashboard/delivery-request/:id', {
            params: { id: deliveryRequest.id },
          }),
        );

        queryClient.invalidateQueries(
          dashboardDeliveryRequestApis.getKeyByPath('get', '/dashboard/delivery-request'),
        );

        queryClient.invalidateQueries(
          dashboardDeliveryRequestLogApis.getKeyByPath(
            'get',
            '/dashboard/delivery-request-log/:id',
            { params: { id: deliveryRequest.id } },
          ),
        );
      },
    },
  );

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [deliveryRequestURLSubmit],
  });

  const form = useForm(DeliveryRequestURLSubmitForm);

  const { fields } = form.root;

  return (
    <>
      <form
        {...form.bindForm({
          async onSubmit(values) {
            onSubmitSuccess(await deliveryRequestURLSubmit.mutateAsync(values));
          },
        })}
      >
        <div className={classes.dialogContent}>
          <div>{renderAlertsAndSpinnerModal()}</div>

          <IndexedFormContainer size="lg">
            <IndexedLabel field={fields.partner_id} />
            <div>
              <TextInput
                field={fields.partner_id}
                inputOptions={{
                  type: 'choose',
                  options: partners.map((item) => ({
                    label: item.name_en_short,
                    value: item.id,
                    labelIcon: <PartnerLink partnerId={item.id} />,
                  })),
                }}
              />
            </div>
          </IndexedFormContainer>
        </div>

        <ActionableDialogActions justifyContent="center">
          <Button
            color="primary"
            size="small"
            text={formStrings.prev}
            type="button"
            shape="round"
            onClick={onClose}
            style={{ width: 100 }}
          />
          <Button
            type="submit"
            color="primary"
            size="small"
            shape="round"
            text={strings.create}
            style={{ width: 100 }}
          />
        </ActionableDialogActions>
      </form>
    </>
  );
};

export const DeliveryRequestURLSubmitDialog: React.FC<DeliveryRequestURLSubmitDialogProps> = (
  props,
) => {
  const { deliveryRequest, open, onClose } = props;

  const [step, setStep] = useState<'INPUT' | 'SUBMIT_RESULT'>('INPUT');

  const [copySuccess, setCopySuccess] = useState(false);

  const [submitResult, setSubmitResult] = useState<DeliveryRequestURLSubmitResult[]>();

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const truckCompaniesQuery = dashboardPartnerApis.useGet('/dashboard/partner', undefined, {
    enabled: open,
  });

  const canSubmit = useMemo(
    () => DeliveryRequestHelper.canSubmit(deliveryRequest),
    [deliveryRequest],
  );

  const renderErrors = () => {
    if (canSubmit._tag === 'Left') {
      return (
        <>
          <div
            className={clsx(classes.middleText, classes.textGray500)}
            style={{ padding: '2rem' }}
          >
            {strings.cannotURLSubmitDeliveryRequest}

            <ul>
              {canSubmit.left.messages.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>

            <Button
              color="primary"
              style={{ width: 100, marginTop: 50 }}
              text="OK"
              size="small"
              shape="round"
              onClick={onClose}
            />
          </div>
        </>
      );
    }
  };

  return (
    <ActionableDialog open={open} onClose={onClose} size="lg">
      <ActionableDialogTitle>{strings.title}</ActionableDialogTitle>

      {canSubmit._tag === 'Right' && (
        <>
          {step === 'INPUT' && (
            <QueryContainer queries={[truckCompaniesQuery]}>
              <DeliveryRequestURLSubmitDialogContent
                {...props}
                partners={truckCompaniesQuery.data ?? []}
                onSubmitSuccess={(v) => {
                  setSubmitResult(v);
                  setStep('SUBMIT_RESULT');
                }}
              />
            </QueryContainer>
          )}

          {step === 'SUBMIT_RESULT' && (
            <>
              <ActionableDialogContent>
                <div
                  className={clsx(classes.textGray700, classes.middleText)}
                  style={{ paddingTop: 20, paddingBottom: 20 }}
                >
                  <Alert open={copySuccess} severity="success" gutter>
                    {strings.copySuccessMessage}
                  </Alert>

                  <div>{strings.deliveryURLsCreatedPrompt}</div>

                  {submitResult?.map(({ url }) => (
                    <div key={url}>
                      <span className={classes.wordBreakBreakAll}>{url}</span>

                      <button
                        onClick={() => {
                          copy(url);
                          setCopySuccess(true);
                        }}
                        style={{ paddingLeft: 10, verticalAlign: 'sub' }}
                      >
                        <img src={copySvg} />
                      </button>
                    </div>
                  ))}
                </div>
              </ActionableDialogContent>

              <ActionableDialogActions justifyContent="center">
                <Button
                  type="submit"
                  color="primary"
                  size="small"
                  shape="round"
                  text={formStrings.prev}
                  style={{ width: 100 }}
                  onClick={() => {
                    setStep('INPUT');
                    setCopySuccess(false);
                  }}
                />
              </ActionableDialogActions>
            </>
          )}
        </>
      )}

      {renderErrors()}
    </ActionableDialog>
  );
};
